import { useCallback, useEffect, useRef } from 'react';
import { useStateIfMounted } from 'use-state-if-mounted';
import {
  getEstheticianFullName,
  getFirstName,
  getClientFullName,
} from 'components/shared/Twilio';
import graphql from 'lib/api/graphql';
import sendSMS from 'features/messaging/graphql/mutations/send-sms.graphql';
import {
  smsFromEstheticianToClient,
  smsFromClientToEsthetician,
} from '../../config/textMessages';
import { useAlerts } from 'providers';
import { useSingleUser } from 'features/users';
import { Conversation as TwilioConversation } from '@twilio/conversations';

export const useManageTwilioMessages = ({
  conversationsByConversationSID,
  setConversationsByConversationSID,
  identity,
}) => {
  const { user } = useSingleUser(identity);
  const { show } = useAlerts();
  // Ref needed for closures so calling functions have access to most recent conversation object
  const conversationRef = useRef(conversationsByConversationSID);

  useEffect(() => {
    conversationRef.current = conversationsByConversationSID;
  }, [conversationsByConversationSID]);

  /**
   * A lazily loaded map of Twilio Message instances, keyed by conversation
   * SID. If the conversation SID is not present, it hasn't been loaded yet:
   * call getMessagesForConversationSID with the conversation SID.
   *
   * @type {[
   *  { [conversationSID: string]: {
   *      messages: import('@twilio/conversations/lib/message').Message[],
   *      loading: boolean
   *    }
   *  },
   *  import('react').Dispatch<
   *    import('react').SetStateAction<{
   *      [conversationSID: string]: {
   *         messages: import('@twilio/conversations/lib/message').Message[],
   *         loading: boolean
   *      }
   *    }>
   *  >
   * ]} - A map of Twilio Message instance arrays, keyed by conversation SID
   */
  const [messagesByConversationSID, _setMessagesByConversationSID] =
    useStateIfMounted({});

  /**
   * Update the conversations state with the Conversation of the newly added
   * message.
   *
   * The timeout solves the same bug as before where React can't see
   * state updates in Conversation class instances due to values only being
   * accessible via getter.
   */
  const _handleMessageAdded = message => {
    setTimeout(() => {
      setConversationsByConversationSID(previousState => {
        const newConversations = {
          [message.conversation.sid]: message.conversation,
          ...previousState,
        };

        return newConversations;
      });

      /**
       * If the message author is not the currently logged in user, notify the
       * current user that they received a new message.
       */
      if (message.author !== user.id) show('You have received a new message!');

      /**
       * If the user hasn't lazily loaded the messages for a conversation
       * that is receiving a new message, load them now--that'll take care of
       * making all messages available to read, including the new one; otherwise,
       * append the new message to the already existing list.
       */

      if (!messagesByConversationSID[message.conversation.sid])
        return getMessagesForConversationSID(message.conversation.sid);

      _setMessagesByConversationSID(previousState => {
        const newMessages = {
          ...previousState,
          [message.conversation.sid]: {
            ...previousState[message.conversation.sid],
            messages: [
              ...(previousState[message.conversation.sid].messages ?? []),
              message,
            ],
          },
        };

        return newMessages;
      });
    }, 100);
  };

  /**
   * Bind 'messageAdded' event to the Conversation, first preventing
   * duplicate listeners.
   */
  const _bindMessageAddedEventListener = useCallback(
    async conversation => {
      conversation?.removeAllListeners('messageAdded');
      conversation.on('messageAdded', message => {
        _handleMessageAdded(message);
      });

      const existing =
        conversationRef.current[conversation.id]?.source === 'sdk';
      if (existing) return;

      // Update conversation attributes if from the server
      if (conversation instanceof TwilioConversation) {
        const participants = await conversation.getParticipants();
        const mobileParticipant = participants.find(
          ({ type }) => type === 'sms'
        );
        setConversationsByConversationSID(previousState => {
          const newConversations = {
            ...previousState,
            [conversation.sid]: conversation,
          };
          newConversations[conversation.sid].source = 'sdk';
          newConversations[conversation.sid].hasMobileParticipants =
            mobileParticipant;
          newConversations[conversation.sid].client =
            previousState[conversation.sid].client;
          return newConversations;
        });
      }
    },
    [
      conversationsByConversationSID,
      messagesByConversationSID,
      _handleMessageAdded,
    ]
  );

  /**
   * Exposes an interface to lazy load all messages for a specified
   * conversation SID.
   * @returns {Promise<void>}
   */
  const getMessagesForConversationSID = useCallback(async conversationSid => {
    /** Set a loading state for the conversation */
    _setMessagesByConversationSID(previousState => ({
      ...previousState,
      [conversationSid]: {
        ...previousState[conversationSid],
        loading: true,
      },
    }));

    const conversation = conversationRef.current[conversationSid];
    const messagesCount = conversation?.getMessagesCount
      ? await conversation.getMessagesCount()
      : conversation?.unreadMessagesCount;

    // There are no messages bro, why try and load them?
    if (
      !conversation ||
      !conversation?.source ||
      conversation?.source === 'server' ||
      !messagesCount
    )
      return _setMessagesByConversationSID(previousState => ({
        ...previousState,
        [conversationSid]: {
          loading: false,
          messages: [],
        },
      }));

    const messages = conversation?.getMessages
      ? await conversation.getMessages(messagesCount)
      : {};

    _setMessagesByConversationSID(previousState => ({
      ...previousState,
      [conversationSid]: {
        loading: false,
        messages: messages.items ?? [],
      },
    }));
  }, []);

  const _sendSMS = useCallback(
    async (messageIndex, conversation) => {
      const participants = await conversation.getParticipants();
      const count = await conversation.getMessagesCount();
      const participantPartner = participants.find(
        participant =>
          participant.type !== 'sms' && participant.identity !== user.id
      );

      const mobileParticipant = participants.find(({ type }) => type === 'sms');

      // If we have a mobile participant and are the esty.
      // This will text them directly.
      if (user.role === 'esthetician' && mobileParticipant) return;

      if (participantPartner) {
        const partnerId = participantPartner.identity;

        const sms =
          user.role === 'esthetician'
            ? smsFromEstheticianToClient(
                getEstheticianFullName(conversation.friendlyName)
              )
            : smsFromClientToEsthetician(
                getFirstName(getClientFullName(conversation.friendlyName))
              );

        await graphql(sendSMS, {
          conversationId: conversation.sid,
          messageIndex,
          totalMessageCount: count,
          user: partnerId,
          sms,
        });
      }
    },
    [identity, user]
  );

  /**
   * Exposes an interface to send a message within a specified conversation.
   * @param {string} conversationSID - The conversation SID in which to send the message
   * @param {string} body - The body of the message
   */
  const sendMessage = useCallback(
    async (conversationSID, messageContent) => {
      const conversation = conversationRef.current[conversationSID];
      const newMessageIndex = await conversation.sendMessage(messageContent);

      await Promise.all([
        conversation.advanceLastReadMessageIndex(newMessageIndex),
        _sendSMS(newMessageIndex, conversation),
      ]);
    },
    [conversationsByConversationSID]
  );

  return {
    messagesByConversationSID,
    getMessagesForConversationSID,
    sendMessage,
    _bindMessageAddedEventListener,
  };
};
